import { CheckOutlined, CloseOutlined, DownOutlined, LoadingOutlined, RightOutlined } from "@ant-design/icons";
import { getToolName, type DynamicToolUIPart, type ToolUIPart } from "ai";
import { useState } from "react";

// TRE-2313 — praca narzędzi jako lista kroków (wzór: AI Elements ChainOfThought / Tool).
// W trakcie odpowiedzi lista jest rozwinięta, a trwający krok migocze (Shimmer). Po
// zakończeniu zwija się do jednej linii z liczbą kroków.

type ToolPart = ToolUIPart | DynamicToolUIPart;

// Mastra podaje narzędzie pod kluczem z obiektu `tools`, starsze rozmowy mogą mieć `id`.
const STEP_LABELS: Record<string, string> = Object.fromEntries(
  (
    [
      ["findClientTool", "find-client", "Szukam klientki"],
      ["getClientProfileForTrainerTool", "get-client-profile", "Czytam profil z ankiety"],
      ["getClientWeekPlanTool", "get-client-plan", "Sprawdzam plan i dietę"],
      ["getClientMealDayTool", "get-client-meal-day", "Sprawdzam jadłospis"],
      ["getClientProgressTool", "get-client-progress", "Sprawdzam postępy"],
      ["getClientActivityTool", "get-client-activity", "Sprawdzam aktywność"],
      ["getClientNotesForTrainerTool", "get-client-notes", "Czytam notatki"],
      ["getClientAccessTool", "get-client-access", "Sprawdzam dostęp i płatności"],
      ["getTrainerClientsTool", "get-trainer-clients", "Pobieram klientki trenera"],
      ["getClientsNeedingAttentionTool", "get-clients-needing-attention", "Szukam klientek do zajęcia się"],
      ["getProblemConversationsTool", "get-problem-conversations", "Przeglądam rozmowy"],
      ["getClientConversationTool", "get-client-conversation", "Czytam rozmowę"],
      ["getTeamResponseTimesTool", "get-team-response-times", "Sprawdzam czas odpowiedzi"],
      ["searchHelpArticlesTool", "search-help-articles", "Szukam w centrum pomocy"],
      ["getHelpArticleTool", "get-help-article", "Czytam materiał z pomocy"],
    ] as const
  ).flatMap(([key, id, label]) => [
    [key, label],
    [id, label],
  ]),
);

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/** Krótki dopisek do etykiety: czego szukał albo co pobrał. */
const stepDetail = (part: ToolPart): string | null => {
  const input = record(part.input);
  const name = getToolName(part);
  if ((name === "findClientTool" || name === "find-client") && typeof input.query === "string")
    return `„${input.query}”`;
  if (
    (name === "searchHelpArticlesTool" || name === "search-help-articles") &&
    typeof input.query === "string" &&
    input.query
  )
    return `„${input.query}”`;
  if ((name === "getClientActivityTool" || name === "get-client-activity") && typeof input.days === "number")
    return `${input.days} dni`;
  if ((name === "getClientMealDayTool" || name === "get-client-meal-day") && typeof input.date === "string")
    return input.date;
  if ((name === "getHelpArticleTool" || name === "get-help-article") && typeof input.slug === "string")
    return input.slug;
  return null;
};

const isDone = (part: ToolPart) => part.state === "output-available";
const isFailed = (part: ToolPart) =>
  part.state === "output-error" ||
  part.state === "output-denied" ||
  (part.state === "output-available" && typeof record(part.output).error === "string");

const StepIcon = ({ part }: { part: ToolPart }) => {
  if (isFailed(part)) return <CloseOutlined className="text-[11px] text-red-500" />;
  if (isDone(part)) return <CheckOutlined className="text-[11px] text-gray-400" />;
  return <LoadingOutlined className="text-[11px] text-gray-500" />;
};

/** Nazwa kroku, który właśnie trwa (dla wskaźnika pracy), albo `null`. */
export const activeStepLabel = (parts: ToolPart[]): string | null => {
  const running = parts.findLast((part) => !isDone(part) && !isFailed(part));
  return running ? (STEP_LABELS[getToolName(running)] ?? null) : null;
};

export const TrainerAssistantSteps = ({ parts, streaming }: { parts: ToolPart[]; streaming: boolean }) => {
  const [expanded, setExpanded] = useState<boolean | null>(null);
  if (!parts.length) return null;

  const running = streaming && parts.some((part) => !isDone(part) && !isFailed(part));
  // Rozwinięte w trakcie pracy, zwinięte po niej - chyba że trener kliknął sam.
  const open = expanded ?? running;
  const failed = parts.filter(isFailed).length;

  return (
    <div className="not-typeset mb-2 text-[13px] text-gray-500">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-md py-0.5 text-gray-500 transition-colors hover:text-gray-800"
        onClick={() => setExpanded(!open)}
      >
        {open ? <DownOutlined className="text-[10px]" /> : <RightOutlined className="text-[10px]" />}
        <span>
          {parts.length === 1 ? "1 krok" : `${parts.length} ${parts.length < 5 ? "kroki" : "kroków"}`}
          {failed ? ` · ${failed} z błędem` : ""}
        </span>
      </button>
      {open ? (
        <ol className="m-0 mt-1 list-none space-y-1 border-0 border-l border-solid border-gray-200 py-0.5 pl-3">
          {parts.map((part) => {
            const label = STEP_LABELS[getToolName(part)] ?? getToolName(part);
            const detail = stepDetail(part);
            const active = streaming && !isDone(part) && !isFailed(part);
            return (
              <li key={part.toolCallId} className="flex items-center gap-2 leading-5">
                <StepIcon part={part} />
                <span className={active ? "chat-shimmer" : isFailed(part) ? "text-red-600" : undefined}>
                  {label}
                  {detail ? <span className="text-gray-400"> {detail}</span> : null}
                </span>
              </li>
            );
          })}
        </ol>
      ) : null}
    </div>
  );
};
